import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, InputGroup, Tabs, Tab, Alert, Accordion } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import SegmentedControl from "./SegmentedControl";

const WizardForm = ({ initialData = {} }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyType: initialData.propertyType || "villa",
    utgangspris: initialData.utgangspris || "",
    taxeringsvarde: initialData.taxeringsvarde || "",
    pantbrev: initialData.pantbrev || "",
    monthlyCost: initialData.monthlyCost || initialData.driftkostnad || initialData.avgift || "",
    kontantinsatsType: initialData.kontantinsatsType || "percentage",
    kontantinsatsAmount: initialData.kontantinsatsAmount || "",
    kontantinsatsPercentage: initialData.kontantinsatsPercentage || 10,
    mortgageInterestRate: initialData.mortgageInterestRate || 2.25,
    isDebtToIncomeHigh: initialData.isDebtToIncomeHigh || false,
    amortizationType: initialData.amortizationType || "automatic",
    customAmortizationRate: initialData.customAmortizationRate || 1.0,
  });

  const [errors, setErrors] = useState({});

  // Swedish market defaults
  const getSwedishDefaults = (utgangspris) => {
    const price = parseFloat(utgangspris) || 0;
    return {
      taxeringsvarde: Math.round(price * 0.74), // Typical 74% of purchase price
      pantbrev: Math.round(price * 0.85 * 0.75), // 75% of 85% LTV
      driftkostnad: 3000, // Typical monthly operating cost
      kontantinsatsPercentage: 10, // Standard down payment (April 1, 2026)
      mortgageInterestRate: 2.25, // Current market rate
    };
  };

  // Removed auto-fill behavior - let users enter their own values

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize numeric inputs
    const numericFields = ['utgangspris', 'taxeringsvarde', 'pantbrev', 'monthlyCost', 'kontantinsatsAmount', 'mortgageInterestRate', 'annualIncome', 'customAmortizationRate'];
    let sanitizedValue = value;
    
    if (numericFields.includes(name)) {
      // Remove all non-numeric characters except dots and commas (including spaces from formatting)
      sanitizedValue = value
        .replace(/[^0-9.,]/g, "")
        .replace(",", ".");
      
      // Prevent multiple dots
      if ((sanitizedValue.match(/\./g) || []).length > 1) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handlePropertyTypeChange = (propertyType) => {
    setFormData(prev => {
      const newData = { ...prev, propertyType };
      
      // Clear fields that are not relevant to the new property type
      if (propertyType === "bostadsratt") {
        // Clear villa-specific fields when switching to bostadsrätt
        newData.taxeringsvarde = "";
        newData.pantbrev = "";
      } else if (propertyType === "villa") {
        // Clear bostadsrätt-specific fields when switching to villa
        // monthlyCost will be used for both, so we keep it
      }
      
      return newData;
    });
  };

  const formatInputValue = (value, fieldName) => {
    const numericFields = ['utgangspris', 'taxeringsvarde', 'pantbrev', 'monthlyCost', 'kontantinsatsAmount'];
    
    if (!numericFields.includes(fieldName) || !value) return value;
    
    // Split by decimal point if it exists
    const parts = value.toString().split('.');
    
    // Format the integer part with spaces as thousand separators
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    // Return formatted value with decimal part if it exists
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  };

  const handleSliderChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Step 1 is just property type selection - no validation needed
      // Property type defaults to "villa" so it's always valid
    }
    
    if (step === 2) {
      if (!formData.utgangspris || parseFloat(formData.utgangspris) <= 0) {
        newErrors.utgangspris = "Utgångspris krävs";
      }
      
      // Only validate taxeringsvärde and pantbrev for Villa
      if (formData.propertyType === "villa") {
        if (!formData.taxeringsvarde || parseFloat(formData.taxeringsvarde) <= 0) {
          newErrors.taxeringsvarde = "Taxeringsvärde krävs";
        }
        if (!formData.pantbrev || parseFloat(formData.pantbrev) < 0) {
          newErrors.pantbrev = "Pantbrev krävs (0 eller mer)";
        }
      }
      
      // Monthly cost is required for both property types
      if (!formData.monthlyCost || parseFloat(formData.monthlyCost) < 0) {
        newErrors.monthlyCost = formData.propertyType === "villa" ? "Driftkostnad krävs" : "Månadsavgift krävs";
      }
    }
    
    if (step === 3) {
      if (formData.kontantinsatsType === "fixed" && (!formData.kontantinsatsAmount || parseFloat(formData.kontantinsatsAmount) <= 0)) {
        newErrors.kontantinsatsAmount = "Kontantinsats krävs";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        completeWizard();
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const completeWizard = () => {
    const utgangspris = parseFloat(formData.utgangspris);
    const kontantinsats = formData.kontantinsatsType === "percentage" 
      ? utgangspris * formData.kontantinsatsPercentage / 100
      : parseFloat(formData.kontantinsatsAmount);
    
    const loanAmount = utgangspris - kontantinsats;
    const ltvRatio = (loanAmount / utgangspris) * 100;
    
    // Calculate required amortization based on LTV
    let ltvAmortizationRate = 0;
    if (ltvRatio > 70) {
      ltvAmortizationRate = 2.0; // 2% per year
    } else if (ltvRatio > 50) {
      ltvAmortizationRate = 1.0; // 1% per year
    }
    
    // Add debt-to-income based amortization
    let debtToIncomeAmortizationRate = 0;
    if (formData.isDebtToIncomeHigh) {
      debtToIncomeAmortizationRate = 1.0; // Additional 1% per year
    }
    
    const requiredAmortizationRate = ltvAmortizationRate + debtToIncomeAmortizationRate;

    // Use custom amortization rate if selected
    const finalAmortizationRate = formData.amortizationType === "custom" 
      ? parseFloat(formData.customAmortizationRate) 
      : requiredAmortizationRate;

    // Create URL parameters for calculator page
    const params = new URLSearchParams();
    
    // Add form data to URL
    if (formData.propertyType && formData.propertyType !== "villa") params.set('propertytype', formData.propertyType);
    if (formData.utgangspris && formData.utgangspris !== 5000000) params.set('price', formData.utgangspris);
    if (formData.taxeringsvarde && formData.taxeringsvarde !== 3702000) params.set('taxvalue', formData.taxeringsvarde);
    if (formData.pantbrev && formData.pantbrev !== 2502000) params.set('mortgagecert', formData.pantbrev);
    if (formData.monthlyCost && formData.monthlyCost !== 3000) params.set('monthlycost', formData.monthlyCost);
    if (formData.isDebtToIncomeHigh) params.set('debttoincomehigh', formData.isDebtToIncomeHigh);
    if (formData.kontantinsatsType === "fixed" && formData.kontantinsatsAmount) params.set('downpayment', formData.kontantinsatsAmount);
    if (formData.mortgageInterestRate && formData.mortgageInterestRate !== 2.25) params.set('mortgagerate', formData.mortgageInterestRate);
    
    // Add state parameters
    if (formData.kontantinsatsPercentage && formData.kontantinsatsPercentage !== 15) params.set('percentage', formData.kontantinsatsPercentage);
    if (formData.kontantinsatsType && formData.kontantinsatsType !== "percentage") params.set('downpaymenttype', formData.kontantinsatsType);
    if (finalAmortizationRate && finalAmortizationRate !== 2.0) params.set('amortizationpct', finalAmortizationRate);
    
    // Navigate to calculator with parameters
    const urlPath = params.toString() ? `/calculator?${params.toString()}` : '/calculator';
    navigate(urlPath);
    
    // Track wizard completion
    if (window.plausible) {
      window.plausible('Wizard Completed', {
        props: {
          price: formData.utgangspris,
          down_payment_type: formData.kontantinsatsType,
          interest_rate: formData.mortgageInterestRate
        }
      });
    }
  };

  const formatCurrency = (value) => {
    if (isNaN(value) || value === "") return "";
    return new Intl.NumberFormat("sv-SE").format(value);
  };

  const calculateLTV = () => {
    const utgangspris = parseFloat(formData.utgangspris) || 0;
    const kontantinsats = formData.kontantinsatsType === "percentage" 
      ? utgangspris * formData.kontantinsatsPercentage / 100
      : parseFloat(formData.kontantinsatsAmount) || 0;
    
    if (utgangspris <= 0) return 0;
    return ((utgangspris - kontantinsats) / utgangspris) * 100;
  };

  const getAmortizationRequirement = () => {
    const ltv = calculateLTV();
    const isDebtToIncomeHigh = formData.isDebtToIncomeHigh || false;
    
    // LTV rule
    let ltvAmortization = "";
    if (ltv > 70) ltvAmortization = "2% per år (belåningsgrad >70%)";
    else if (ltv > 50) ltvAmortization = "1% per år (belåningsgrad 50-70%)";
    else ltvAmortization = "0% (belåningsgrad ≤50%)";

    // Debt-to-income rule removed as of April 1, 2026
    return ltvAmortization;
  };

  return (
    <Container className="wizard-container">
      <Row className="justify-content-center min-vh-100 align-items-center">
        <Col xs={12} md={8} lg={6}>
          <Card className="wizard-card shadow-lg">
            <Card.Header className="wizard-header">
              <h3 className="text-center mb-0">BoendeBeräkning.se</h3>
              <p className="text-center text-muted mb-0">Kom igång med din bostadskalkyl</p>
            </Card.Header>
            <Card.Body className="wizard-body">
              {/* Progress Steps */}
              <div className="wizard-steps mb-4">
                <div className="d-flex justify-content-between">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className={`wizard-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
                      <div className="step-number">{step}</div>
                      <div className="step-label">
                        {step === 1 && "Typ"}
                        {step === 2 && "Bostad"}
                        {step === 3 && "Kontantinsats"}
                        {step === 4 && "Bolån"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 1: Property Type Selection */}
              {currentStep === 1 && (
                <div className="wizard-step-content">
                  <div className="text-center mb-4">
                    <h5 className="mb-3">Välj typ av bostad</h5>
                    <p className="text-muted">
                      Välj vilken typ av bostad du vill beräkna kostnader för. Detta påverkar vilka kostnader som är relevanta.
                    </p>
                  </div>
                  
                  <Form.Group className="mb-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <input 
                          type="radio" 
                          className="btn-check" 
                          name="propertyType" 
                          id="wizard-villa-card" 
                          value="villa"
                          checked={formData.propertyType === "villa"}
                          onChange={() => handlePropertyTypeChange("villa")}
                          autoComplete="off"
                        />
                        <label 
                          className={`btn property-type-card h-100 w-100 ${formData.propertyType === "villa" ? "btn-primary" : "btn-outline-secondary"}`} 
                          htmlFor="wizard-villa-card"
                        >
                          <div className="text-center py-3">
                            <div className="property-icon mb-3" style={{fontSize: "2.5rem"}}>🏠</div>
                            <div className="fw-bold mb-2">Villa/Äganderätt</div>
                            <div className="text-muted small">
                              • Pantbrev och lagfartskostnader<br/>
                              • Driftkostnader (el, vatten, värme)<br/>
                              • Taxeringsvärde påverkar lagfart
                            </div>
                          </div>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input 
                          type="radio" 
                          className="btn-check" 
                          name="propertyType" 
                          id="wizard-bostadsratt-card" 
                          value="bostadsratt"
                          checked={formData.propertyType === "bostadsratt"}
                          onChange={() => handlePropertyTypeChange("bostadsratt")}
                          autoComplete="off"
                        />
                        <label 
                          className={`btn property-type-card h-100 w-100 ${formData.propertyType === "bostadsratt" ? "btn-primary" : "btn-outline-secondary"}`} 
                          htmlFor="wizard-bostadsratt-card"
                        >
                          <div className="text-center py-3">
                            <div className="property-icon mb-3" style={{fontSize: "2.5rem"}}>🏢</div>
                            <div className="fw-bold mb-2">Bostadsrätt</div>
                            <div className="text-muted small">
                              • Inga pantbrev eller lagfartskostnader<br/>
                              • Månadsavgift till föreningen<br/>
                              • Enklare köpprocess
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </Form.Group>

                </div>
              )}

              {/* Step 2: Property Details */}
              {currentStep === 2 && (
                <div className="wizard-step-content">
                  <h5 className="mb-3">Detaljer om bostaden</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Utgångspris *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        name="utgangspris"
                        value={formatInputValue(formData.utgangspris, 'utgangspris')}
                        onChange={handleInputChange}
                        placeholder="5 000 000"
                        isInvalid={!!errors.utgangspris}
                      />
                      <InputGroup.Text>kr</InputGroup.Text>
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.utgangspris}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {formData.propertyType === "villa" && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Taxeringsvärde *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="taxeringsvarde"
                            value={formatInputValue(formData.taxeringsvarde, 'taxeringsvarde')}
                            onChange={handleInputChange}
                            placeholder="3 700 000"
                            isInvalid={!!errors.taxeringsvarde}
                          />
                          <InputGroup.Text>kr</InputGroup.Text>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.taxeringsvarde}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          Används för beräkning av lagfartskostnad
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Befintliga pantbrev *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="pantbrev"
                            value={formatInputValue(formData.pantbrev, 'pantbrev')}
                            onChange={handleInputChange}
                            placeholder="2 500 000"
                            isInvalid={!!errors.pantbrev}
                          />
                          <InputGroup.Text>kr</InputGroup.Text>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.pantbrev}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          Ange 0 om inga pantbrev finns sedan tidigare
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Driftkostnad per månad *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="monthlyCost"
                            value={formatInputValue(formData.monthlyCost, 'monthlyCost')}
                            onChange={handleInputChange}
                            placeholder="3 000"
                            isInvalid={!!errors.monthlyCost}
                          />
                          <InputGroup.Text>kr/mån</InputGroup.Text>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.monthlyCost}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          El, vatten, värme, försäkring och övriga kostnader
                        </Form.Text>
                      </Form.Group>
                    </>
                  )}

                  {formData.propertyType === "bostadsratt" && (
                    <Form.Group className="mb-3">
                      <Form.Label>Månadsavgift *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          name="monthlyCost"
                          value={formatInputValue(formData.monthlyCost, 'monthlyCost')}
                          onChange={handleInputChange}
                          placeholder="4 500"
                          isInvalid={!!errors.monthlyCost}
                        />
                        <InputGroup.Text>kr/mån</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {errors.monthlyCost}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Månadsavgift till bostadsrättsföreningen
                      </Form.Text>
                    </Form.Group>
                  )}
                </div>
              )}

              {/* Step 3: Down Payment */}
              {currentStep === 3 && (
                <div className="wizard-step-content">
                  <h5 className="mb-3">Kontantinsats</h5>
                  <p className="text-muted small mb-3">
                    Standard från april 2026: 10% av köpeskillingen
                  </p>

                  <Tabs
                    activeKey={formData.kontantinsatsType}
                    onSelect={(key) => setFormData(prev => ({ ...prev, kontantinsatsType: key }))}
                    className="mb-3"
                  >
                    <Tab eventKey="percentage" title="Procent">
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Kontantinsats ({formData.kontantinsatsPercentage}%)
                        </Form.Label>
                        <Form.Range
                          min="5"
                          max="50"
                          value={formData.kontantinsatsPercentage}
                          onChange={(e) => handleSliderChange('kontantinsatsPercentage', e.target.value)}
                        />
                        <div className="d-flex justify-content-between text-muted small">
                          <span>5%</span>
                          <span>50%</span>
                        </div>
                        {formData.utgangspris && (
                          <div className="mt-2 text-center">
                            <strong>
                              {formatCurrency(parseFloat(formData.utgangspris) * formData.kontantinsatsPercentage / 100)} kr
                            </strong>
                          </div>
                        )}
                      </Form.Group>
                    </Tab>
                    <Tab eventKey="fixed" title="Fast belopp">
                      <Form.Group className="mb-3">
                        <Form.Label>Kontantinsats belopp *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="kontantinsatsAmount"
                            value={formatInputValue(formData.kontantinsatsAmount, 'kontantinsatsAmount')}
                            onChange={handleInputChange}
                            placeholder="750 000"
                            isInvalid={!!errors.kontantinsatsAmount}
                          />
                          <InputGroup.Text>kr</InputGroup.Text>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.kontantinsatsAmount}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Tab>
                  </Tabs>

                  {formData.utgangspris && (
                    <Alert variant="info" className="mb-3">
                      <div className="small">
                        <strong>Belåningsgrad:</strong> {calculateLTV().toFixed(1)}%<br/>
                        <strong>Lånebelopp:</strong> {formatCurrency(parseFloat(formData.utgangspris) - (
                          formData.kontantinsatsType === "percentage" 
                            ? parseFloat(formData.utgangspris) * formData.kontantinsatsPercentage / 100
                            : parseFloat(formData.kontantinsatsAmount) || 0
                        ))} kr
                      </div>
                    </Alert>
                  )}
                </div>
              )}

              {/* Step 4: Mortgage */}
              {currentStep === 4 && (
                <div className="wizard-step-content">
                  <h5 className="mb-3">Bolåneinställningar</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Bolåneränta ({formData.mortgageInterestRate}%)
                    </Form.Label>
                    <Form.Range
                      min="0.5"
                      max="8.0"
                      step="0.25"
                      value={formData.mortgageInterestRate}
                      onChange={(e) => handleSliderChange('mortgageInterestRate', e.target.value)}
                    />
                    <div className="d-flex justify-content-between text-muted small">
                      <span>0.5%</span>
                      <span>8.0%</span>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Amortering</Form.Label>

                    <div className="mb-2">
                      <Form.Check
                        type="radio"
                        id="amortization-automatic"
                        name="amortizationType"
                        value="automatic"
                        checked={formData.amortizationType === "automatic"}
                        onChange={handleInputChange}
                        label="Automatisk enligt svenska regler"
                      />
                      <Form.Check
                        type="radio"
                        id="amortization-custom"
                        name="amortizationType"
                        value="custom"
                        checked={formData.amortizationType === "custom"}
                        onChange={handleInputChange}
                        label="Anpassad amortering"
                      />
                    </div>
                    
                    {formData.amortizationType === "custom" && (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Amortering ({formData.customAmortizationRate}%)
                        </Form.Label>
                        <Form.Range
                          min="0"
                          max="10"
                          step="0.1"
                          value={formData.customAmortizationRate}
                          onChange={(e) => handleSliderChange('customAmortizationRate', e.target.value)}
                        />
                        <div className="d-flex justify-content-between text-muted small">
                          <span>0%</span>
                          <span>10%</span>
                        </div>
                      </Form.Group>
                    )}
                  </Form.Group>

                  <Alert variant="info" className="mb-3">
                    <div className="small">
                      <strong>Amortering:</strong> {(() => {
                        const ltv = calculateLTV();
                        const isDebtToIncomeHigh = formData.isDebtToIncomeHigh || false;
                        let ltvRate = 0;
                        if (ltv > 70) ltvRate = 2;
                        else if (ltv > 50) ltvRate = 1;
                        const debtToIncomeRate = isDebtToIncomeHigh ? 1 : 0;
                        return `${ltvRate + debtToIncomeRate}% per år`;
                      })()}
                      {formData.amortizationType === "custom" && (
                        <>
                          <br/><strong>Vald amortering:</strong> {formData.customAmortizationRate}%
                        </>
                      )}
                      
                      <Accordion className="mt-2">
                        <Accordion.Item eventKey="0">
                          <Accordion.Header>
                            <small>
                              <strong>Svenska amorteringsregler (april 2026)</strong>
                            </small>
                          </Accordion.Header>
                          <Accordion.Body>
                            <small className="text-muted">
                              <strong>Amorteringskrav baserat på belåningsgrad:</strong><br/>
                              • Belåningsgrad över 70 % → minst 2 % per år<br/>
                              • Belåningsgrad 50–70 % → minst 1 % per år<br/>
                              • Belåningsgrad under 50 % → inget krav<br/>
                              <hr className="my-2"/>
                              <strong>Vad är belåningsgrad?</strong><br/>
                              <em>Belåningsgrad</em> = Lånets belopp / Bostadens värde (köpeskillingen)
                              <br/><br/>
                              <em>Exempel:</em> Om du köper för 3 000 000 kr och lånar 2 700 000 kr är belåningsgraden 90 %.
                            </small>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </div>
                  </Alert>

                  <div className="summary-box p-3 bg-light rounded mb-3">
                    <h6 className="mb-2">Sammanfattning</h6>
                    <div className="row small">
                      <div className="col-6">
                        <strong>Utgångspris:</strong><br/>
                        {formatCurrency(formData.utgangspris)} kr
                      </div>
                      <div className="col-6">
                        <strong>Kontantinsats:</strong><br/>
                        {formData.kontantinsatsType === "percentage" 
                          ? `${formData.kontantinsatsPercentage}% (${formatCurrency(parseFloat(formData.utgangspris) * formData.kontantinsatsPercentage / 100)} kr)`
                          : `${formatCurrency(formData.kontantinsatsAmount)} kr`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="wizard-navigation mt-4">
                <div className="d-flex justify-content-between">
                  <Button 
                    variant="outline-secondary" 
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    Tillbaka
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={nextStep}
                  >
                    {currentStep === 4 ? "Slutför" : "Nästa"}
                  </Button>
                </div>
              </div>

              {/* Privacy Disclaimer */}
              <div className="text-center mt-4 pt-3 border-top">
                <small className="text-muted">
                  <strong>BoendeBeräkning.se värnar om din integritet</strong><br/>
                  All information behandlas lokalt i din webbläsare och skickas aldrig till någon server.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default WizardForm;